import type { OcrPage } from '../types';

/**
 * The single-page fallback used wherever a result carries no `pages` array —
 * older results predating the sibling-pages projection. One copy, because three
 * screens rebuilt it by hand and each would otherwise have to be fixed
 * separately.
 *
 * It has to be able to REFUSE. An OcrResult whose scan chain never reached a
 * container (a lab/direct upload) has `batch_page_record_id: null`, so
 * `OcrResultResource` projects `pages: []`, `batch_id: null`, `image_url: null`
 * and `pdf_url: null`. Fabricating a page out of that null gave the search
 * viewer a page labelled `#null` over a grey placeholder — worse than an honest
 * "no pages" state, because it claims a document exists and then fails to draw
 * it.
 *
 * The guard is the page's IDENTITY, not whether an image is reachable: on the
 * results page a page row also carries the run's status and extracted fields,
 * which are worth rendering for a row whose image is merely missing.
 */
export interface LegacyViewerPageSource {
  batch_page_record_id: number | null;
  page_number?: string | null;
  image_url?: string | null;
  has_image?: boolean;
}

export function legacyViewerPage(
  result: LegacyViewerPageSource
): OcrPage | null {
  if (result.batch_page_record_id == null) return null;

  return {
    id: result.batch_page_record_id,
    page_number: result.page_number ?? '',
    image_url: result.image_url ?? null,
    has_image: result.has_image
  };
}
